import { getCookie } from '@/helpers/cookieMetods';
import type { EntityId } from '../../types/common';
import type { CoinsHistoryRow } from '../../types/coins';
import type { ApiStatusResponse } from '../../types/orders';
import { apiUrl, parseJsonOrThrow } from '../http';

/**
 * Reads the `sc` balance from a `coins.php` JSON response, distinguishing an
 * omitted `sc` (returns `null`) from a present-but-zero balance, so callers can
 * decide whether to refetch after a mutation that omits it. Numeric strings are
 * coerced; non-finite values become `null`.
 */
export const parseCoinsApiScIfPresent = (body: unknown): number | null => {
  if (body === null || typeof body !== 'object') return null;
  if (!Object.prototype.hasOwnProperty.call(body, 'sc')) return null;
  const sc = (body as { sc?: unknown }).sc;
  if (sc === undefined || sc === null) return null;
  const n = Number(sc);
  return Number.isFinite(n) ? n : null;
};

export const getCoins = async (
  userID: EntityId | undefined
): Promise<number> => {
  const token = getCookie('_form_token');
  const body = {
    coins: 'get_user_coins',
    user_id: userID,
    admin_id: userID,
    token,
  };
  try {
    const res = await fetch(apiUrl('/php/coins.php'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await parseJsonOrThrow<unknown>(res);
    return parseCoinsApiScIfPresent(result) ?? 0;
  } catch {
    return 0;
  }
};

export const changeCoins = async (
  userID: EntityId | undefined,
  adminID: EntityId | undefined,
  coins: number,
  comment = '',
  initId?: EntityId
): Promise<ApiStatusResponse & Record<string, unknown>> => {
  const token = getCookie('_form_token');
  const response = await fetch(apiUrl('/php/coins.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      coins,
      user_id: userID,
      admin_id: adminID,
      init_id: initId,
      comment,
      token,
    }),
  });
  return parseJsonOrThrow<ApiStatusResponse & Record<string, unknown>>(
    response
  );
};

export const getCoinsHistory = async (
  userID: EntityId | undefined
): Promise<CoinsHistoryRow[]> => {
  const token = getCookie('_form_token');
  const body = {
    coins: 'get_list_coins',
    user_id: userID,
    admin_id: userID,
    token,
  };
  try {
    const res = await fetch(apiUrl('/php/coins.php'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await parseJsonOrThrow<CoinsHistoryRow[] | null>(res);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};
